import { useCallback, useContext, useEffect, useRef, useState, useMemo } from 'react'
import {
  Grid,
  SearchIcon,
  Typography,
  ListItemText,
  ListItemIcon,
  Stack,
  useTheme,
  useMediaQuery,
  Hidden,
  MenuItem,
  ClickAwayListener
} from '@hermes/web-components'
import useTranslateMessage from '../../../../hooks/useTranslateMessage'
import { SearchProfilesPageContext } from '../../../../providers/SearchProfilesPage'
import NextImage from 'next/image'
import { AppDataContext } from '../../../../providers/AppData'
import { useTimeout } from '../../../../hooks/useTimeout'
import useClickOutside from '../../../../hooks/useClickOutside'
import { SearchTypes } from '../../../../providers/SearchProfilesPage/types'
import { MappedLocation, WebViewProps } from '../../common/types'
import { Location } from '../../../../types'
import { TEST_IDS } from '../../../../__tests__/testUtils/constants'
import useSearchBarConfig from '../../../search/hooks/useSearchBarConfig'
import { newSearchTypesPractices } from '../../../../constants/global'
import {
  StyledIconButton,
  CustomMenuItem,
  StyledAutocomplete,
  LocationSearchTextField,
  StyledMenuList,
  StyledLink,
  TypeMenuItem,
  SearchBarContainer,
  whiteColor,
  SearchButtonContainer,
  StyledSearchIconButton,
  AutocompleteSearchTextField,
  StyledPaper,
  StyledNoOptionText
} from './styles'
import LoadingWheel from '../../../Icons/LoadingWheel'
import AutocompleteResultsModal from '../AutocompleteResultsModal'

type TypeMenuItemProps = {
  title: string
  value: SearchTypes
  isFirstItem: boolean
  isLastItem: boolean
  Icon: string
}

const WebView = ({
  onChangeSearchType,
  onChangeSearch,
  onSearchFieldFocus,
  onSearchFieldBlur,
  onChangeLocationSearch,
  isLocationFieldFocused,
  setIsLocationFieldFocused,
  ...autocompleteProps
}: WebViewProps) => {
  const { language } = useContext(AppDataContext)
  const translate = useTranslateMessage()
  const {
    changeCurrentLocation,
    buildUrl,
    changeSearchTypeUrl,
    resetLocationsAutocomplete,
    state: { locations, filters, specialty, isKeywordChanged, isTypeChanged, isLocationChanged }
  } = useContext(SearchProfilesPageContext)
  const isAlternativeSearchTypesIncluded = useMemo(
    () => filters?.searchType && newSearchTypesPractices.includes(filters?.searchType),
    [filters?.searchType]
  )

  const { searchTypes, selectedSearchType } = useSearchBarConfig()

  const locationsAutocompleteRef = useRef(null)

  const autocompleteLocationMapper = ({
    place_id,
    description,
    name,
    googlePlaceId,
    slug
  }: Location): MappedLocation => ({
    id: place_id || googlePlaceId,
    label: description || name[language],
    slug
  })

  const mappedLocation: MappedLocation[] = locations?.map(autocompleteLocationMapper) || []
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'))

  const [openSearchTypeDropdown, setOpenSearchTypeDropdown] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [isNewLocationSelected, setIsNewLocationSelected] = useState(false)
  const [isTypeFieldHovered, setIsTypeFieldHovered] = useState(false)
  const [isLocationFieldHovered, setIsLocationFieldHovered] = useState(false)
  const [isAutocompleteHovered, setIsAutocompleteHovered] = useState(false)

  const handleSetFocusOnLocation = () => setIsLocationFieldFocused((prev) => !prev)
  const handleCloseSearchTypeDown = () => setOpenSearchTypeDropdown(false)

  useClickOutside(locationsAutocompleteRef, () => {
    if (isDesktop) {
      resetLocationsAutocomplete()
    }
    setIsLocationFieldFocused(false)
  })

  const changeTypeDropdownState = () => setOpenSearchTypeDropdown((prev) => !prev)

  const timeout = useTimeout()

  const onLocationChanged = (newValue: { label: string; id: string; slug?: string | null }) =>
    timeout(() => {
      setIsNewLocationSelected(true)
      changeCurrentLocation(newValue.id, newValue.label, newValue.slug)
    })

  const handleSearch = () => {
    if (!isNewLocationSelected) {
      location.reload()
    }
  }

  const handleSearchType = useCallback(
    (value: SearchTypes) => () => {
      changeTypeDropdownState()
      onChangeSearchType(value)
    },
    [onChangeSearchType]
  )

  useEffect(() => {
    if (!searchText) {
      setSearchText(decodeURIComponent(filters.location))
    }
    // TODO: clarify deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.location])

  const renderTypeMenuItem = ({ title, value, isFirstItem, isLastItem, Icon }: TypeMenuItemProps) => (
    <StyledLink
      key={`search-type-${value}`}
      href={changeSearchTypeUrl(value)}
      onClick={(e) => {
        e.preventDefault()
        changeTypeDropdownState()
      }}
    >
      <TypeMenuItem value={value} isFirstItem={isFirstItem} isLastItem={isLastItem} onClick={handleSearchType(value)}>
        <Stack direction="row" justifyContent="space-between" p={0.5}>
          <ListItemIcon>
            <NextImage
              src={`/hermes-static/icons/${Icon}.svg`}
              alt="search-type-icon"
              width={22}
              height={22}
              objectFit="none"
              unoptimized
            />
          </ListItemIcon>
          <ListItemText>
            <Typography
              color="text.primary"
              variant="body1"
              textAlign="left"
              sx={filters?.searchType === value ? theme.custom.SearchBar.SelectedMenuItem : {}}
            >
              {title}
            </Typography>
          </ListItemText>
        </Stack>
      </TypeMenuItem>
    </StyledLink>
  )

  return (
    <Grid component={Hidden} implementation="css" justifyContent="center" sm={8}>
      <Grid container>
        <SearchBarContainer
          container
          isAnyOptionSelected={
            openSearchTypeDropdown || autocompleteProps.isSearchFieldFocused || isLocationFieldFocused
          }
          leftTabHovered={
            (isTypeFieldHovered && autocompleteProps.isSearchFieldFocused) ||
            (openSearchTypeDropdown && isAutocompleteHovered)
          }
          rightTabHovered={
            (isLocationFieldHovered && autocompleteProps.isSearchFieldFocused) ||
            (isLocationFieldFocused && isAutocompleteHovered)
          }
        >
          <ClickAwayListener onClickAway={handleCloseSearchTypeDown}>
            <Grid item width="30%">
              <StyledIconButton
                data-testid={TEST_IDS.SEARCH_BAR_PROFILE_TYPE_SELECTOR}
                onClick={() => {
                  if (!isTypeChanged) {
                    changeTypeDropdownState()
                  }
                }}
                disableRipple
                onMouseEnter={() => setIsTypeFieldHovered(true)}
                onMouseLeave={() => setIsTypeFieldHovered(false)}
              >
                {selectedSearchType && (
                  <CustomMenuItem
                    openSearchTypeDropdown={openSearchTypeDropdown}
                    isSearchFieldFocused={autocompleteProps.isSearchFieldFocused}
                    isLocationFieldFocused={isLocationFieldFocused}
                  >
                    <ListItemText sx={{ display: 'flex', flexDirection: 'row', width: '100%' }}>
                      <Typography color="text.primary" variant="body2" textAlign="left">
                        {selectedSearchType.title}
                      </Typography>
                    </ListItemText>
                    {isTypeChanged && <LoadingWheel />}
                  </CustomMenuItem>
                )}

                <StyledMenuList isOpen={openSearchTypeDropdown}>
                  {searchTypes.map(({ value, title, Icon }, index) =>
                    renderTypeMenuItem({
                      value,
                      title,
                      isFirstItem: index === 0,
                      isLastItem: searchTypes.length - 1 === index,
                      Icon
                    })
                  )}
                </StyledMenuList>
              </StyledIconButton>
            </Grid>
          </ClickAwayListener>

          <Grid item width="40%">
            <StyledAutocomplete
              data-testid={TEST_IDS.SEARCH_BAR_CONDITION_SEARCH}
              options={[]}
              disablePortal
              disableClearable
              popupIcon={null}
              value={!isAlternativeSearchTypesIncluded ? specialty.name[language] : ''}
              onMouseEnter={() => setIsAutocompleteHovered(true)}
              onMouseLeave={() => setIsAutocompleteHovered(false)}
              openSearchTypeDropdown={openSearchTypeDropdown}
              renderInput={(props) => (
                <AutocompleteSearchTextField
                  {...props}
                  fullWidth
                  placeholder={translate('search.placeholder.find')}
                  onChange={onChangeSearch}
                  value={specialty.name[language]}
                  onFocus={() => {
                    if (!isKeywordChanged) {
                      onSearchFieldFocus()
                    }
                  }}
                  onBlur={onSearchFieldBlur}
                  isBorderLeftHidden={isTypeFieldHovered || openSearchTypeDropdown}
                  isBorderRightHidden={isLocationFieldHovered || isLocationFieldFocused}
                  isSearchFieldFocused={autocompleteProps.isSearchFieldFocused}
                  openSearchTypeDropdown={openSearchTypeDropdown}
                  isLocationFieldFocused={isLocationFieldFocused}
                  InputProps={{
                    endAdornment: isKeywordChanged ? <LoadingWheel /> : null,
                    sx: {
                      borderWidth: 0,
                      paddingRight: isKeywordChanged ? '9px !important' : ''
                    }
                  }}
                />
              )}
            />
            <AutocompleteResultsModal
              customStyle={
                !!filters?.searchType && newSearchTypesPractices.includes(filters.searchType)
                  ? theme.custom.SearchBar.customPositionDesktopModal
                  : theme.custom.SearchBar.customPositionDesktopModalForDoctify
              }
              onClose={onSearchFieldBlur}
              onChangeSearch={onChangeSearch}
              isOpen={autocompleteProps.isSearchFieldFocused}
              setIsSearchFieldFocused={setIsLocationFieldFocused}
            />
          </Grid>
          <Grid item width="30%">
            <LocationSearchTextField
              data-testid={TEST_IDS.SEARCH_BAR_LOCATIONS_SEARCH}
              ref={locationsAutocompleteRef}
              fullWidth
              value={searchText}
              autoComplete="off"
              onFocus={() => {
                if (!isLocationChanged) {
                  handleSetFocusOnLocation()
                }
              }}
              placeholder={translate('search.placeholder.location')}
              isAnyOptionSelected={openSearchTypeDropdown || autocompleteProps.isSearchFieldFocused}
              onChange={(e) => {
                setIsLocationFieldFocused(true)
                setSearchText(e.target.value)
                onChangeLocationSearch(e)
              }}
              onBlur={() => {
                setSearchText(decodeURIComponent(filters.location))
              }}
              onMouseEnter={() => setIsLocationFieldHovered(true)}
              onMouseLeave={() => setIsLocationFieldHovered(false)}
              isLocationFieldFocused={isLocationFieldFocused}
              sx={{
                '& div': {
                  paddingRight: isLocationChanged ? '10px !important' : ''
                }
              }}
              InputProps={{
                endAdornment: (
                  <>
                    {isLocationChanged && <LoadingWheel />}
                    <StyledPaper open={isLocationFieldFocused}>
                      {mappedLocation.length ? (
                        mappedLocation.map((location) => {
                          const url = buildUrl({
                            location: location.label,
                            slug: location.slug
                          })

                          return (
                            <MenuItem
                              component="a"
                              key={`location-link-${location.id}-${location.label}`}
                              href={url}
                              onClick={() => {
                                setIsLocationFieldFocused(false)
                                onLocationChanged(location)
                              }}
                              sx={{
                                fontSize: '14px',
                                padding: '6px 16px',
                                color: 'text.primary',
                                boxSizing: 'border-box',
                                display: 'flex',
                                justifyContent: 'flex-start',
                                whiteSpace: 'wrap',
                                '&:hover': {
                                  backgroundColor: theme.custom.SearchBar.MenuItem.backgroundColor
                                },
                                ...(decodeURIComponent(filters?.location) === location.label
                                  ? theme.custom.SearchBar.SelectedMenuItem
                                  : {})
                              }}
                            >
                              {location.label}
                            </MenuItem>
                          )
                        })
                      ) : (
                        <StyledNoOptionText>{translate('searchBar.noOption')}</StyledNoOptionText>
                      )}
                    </StyledPaper>
                  </>
                )
              }}
            />
          </Grid>
        </SearchBarContainer>
        <SearchButtonContainer>
          <StyledSearchIconButton size="large" data-testid={TEST_IDS.SEARCH_BAR_SEARCH_BUTTON} onClick={handleSearch}>
            <SearchIcon sx={{ color: whiteColor }} />
          </StyledSearchIconButton>
        </SearchButtonContainer>
      </Grid>
    </Grid>
  )
}

export default WebView
